import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Alert,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { 
  BadgeOutlined,
  AccountTree,
  ViewList,
  Assessment
} from '@mui/icons-material';
import RoleDefinitionList from '../components/roleDefinitions/RoleDefinitionList';
import RoleDefinitionForm from '../components/roleDefinitions/RoleDefinitionForm';
import RoleHierarchy from '../components/roleDefinitions/RoleHierarchy';
import RoleUsageStatistics from '../components/roleDefinitions/RoleUsageStatistics';
import { usePeopleAdmin } from '../context/PeopleAdminContext';
import { useRoleDefinitions } from '../hooks/useRoleDefinitions';

/**
 * Role Definitions tab component for managing role definitions
 * @returns {React.Component} Role Definitions tab content with data table and actions
 */
function RoleDefinitionsTab() {
  // Uncommented - testing if hooks cause lockup
  const { roleDefinitions, isLoadingRoleDefinitions, roleDefinitionsError } = usePeopleAdmin();
  const { getActiveRoles, getInactiveRoles } = useRoleDefinitions();
  
  // Uncommented - testing tabs UI
  const [tabValue, setTabValue] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  


  // Uncommented - testing if hooks cause lockup
  const activeRoles = getActiveRoles();
  const inactiveRoles = getInactiveRoles();
  
  // Uncommented - testing tabs UI
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateClick = () => {
    setSelectedRole(null);
    setFormOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedRole(null);
  };

  const handleFormSuccess = async () => {
    console.log('RoleDefinitionsTab - Form success, closing modal');
    setFormOpen(false);
    setSelectedRole(null);
    // Force a re-render by updating state
    setTabValue(current => current);
  };

  const handleViewUsage = (role) => {
    // TODO: Implement navigation to Location Admin with role filter
  };

  // TEMPORARILY COMMENTED OUT TO DEBUG LOCKUP ISSUE
  // if (isLoadingRoleDefinitions) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }
  //
  // if (roleDefinitionsError) {
  //   return (
  //     <Alert severity="error" sx={{ mb: 3 }}>
  //       Failed to load role definitions: {roleDefinitionsError.message}
  //     </Alert>
  //   );
  // }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Role Definitions
          </Typography>
          {/* Uncommented - testing if hooks cause lockup */}
          <Stack direction="row" spacing={1} mt={1}>
            <Chip 
              label={`${activeRoles.length} Active`} 
              color="success" 
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '22px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
            {inactiveRoles.length > 0 && (
              <Chip 
                label={`${inactiveRoles.length} Inactive`} 
                color="default" 
                size="small" 
                variant="outlined"
                sx={{ 
                  fontSize: '0.75rem',
                  height: '22px',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0
                  }
                }}
              />
            )}
          </Stack>
        </Box>
        <AdminButton
          variant="contained"
          startIcon={<BadgeOutlined />}
          onClick={handleCreateClick}
        >
          Create Role Definition
        </AdminButton>
      </Box>
      
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            icon={<ViewList />} 
            iconPosition="start" 
            label="List View" 
          />
          <Tab 
            icon={<AccountTree />} 
            iconPosition="start" 
            label="Hierarchy View" 
          />
          <Tab 
            icon={<Assessment />} 
            iconPosition="start" 
            label="Usage Statistics" 
          />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <RoleDefinitionList 
          onEdit={handleEditRole}
          onViewUsage={handleViewUsage}
        />
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RoleHierarchy onRoleClick={handleEditRole} />
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <RoleUsageStatistics />
      )}

      {formOpen && (
        <RoleDefinitionForm
          open={formOpen}
          onClose={handleFormClose}
          roleDefinition={selectedRole}
          onSuccess={handleFormSuccess}
        />
      )}
    </Box>
  );
}

export default RoleDefinitionsTab;